import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Radio, Upload, Sparkles, ArrowRight } from "lucide-react";
import TrackCard from "../components/TrackCard";
import ContinueListeningCard from "../components/ContinueListeningCard";
import { useAuth } from "../context/AuthContext";

const HERO_IMG = "https://images.unsplash.com/photo-1766343596600-66b21350d061?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85";

export default function HomePage() {
  const { user } = useAuth();
  const [featured, setFeatured] = useState([]);
  const [recent, setRecent] = useState([]);
  const [rotation, setRotation] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      // Fire all 3 startup calls in parallel — but render whatever succeeds even if one fails.
      // Prevents a transient blip on a single endpoint from wiping the whole page with "Network Error".
      const results = await Promise.allSettled([
        api.get("/tracks", { params: { featured: true, limit: 10 } }),
        api.get("/tracks", { params: { limit: 10 } }),
        api.get("/radio/rotation"),
      ]);
      const [f, r, rot] = results;
      if (f.status === "fulfilled") setFeatured(f.value.data || []);
      if (r.status === "fulfilled") setRecent(r.value.data || []);
      if (rot.status === "fulfilled") setRotation(rot.value.data || []);
      // Only show error if ALL three failed — partial failures degrade silently.
      const failures = results.filter((x) => x.status === "rejected");
      if (failures.length === results.length) {
        setError(formatApiError(failures[0].reason));
      }
    })();
  }, []);

  return (
    <div className="space-y-20 md:space-y-28">
      {/* Hero */}
      <section className="relative -mx-6 md:-mx-12 -mt-6 md:-mt-12 overflow-hidden">
        <div className="relative h-[70vh] min-h-[520px] grain">
          <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#060709] via-[#060709]/85 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#060709] via-transparent to-transparent" />

          <div className="relative h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-center">
            <div className="max-w-2xl">
              <div className="overline text-[#C5A059] mb-5" data-testid="hero-overline">
                ✦ A sanctuary for sound ✦
              </div>
              <h1 className="font-serif text-[#FDFBF7] text-4xl sm:text-5xl lg:text-7xl tracking-tighter leading-[0.95]" data-testid="hero-title">
                Yah-Wave <span className="italic text-[#C5A059]">Radio</span>
              </h1>
              <p className="text-[#A1A4AB] text-base md:text-lg leading-relaxed mt-6 max-w-xl">
                Music and teachings that lift the name above all names. Tune in to the live stream, explore our library, or submit your own righteous sound for the station.
              </p>
              <div className="flex flex-wrap gap-3 mt-8">
                <Link to="/radio" className="btn-primary" data-testid="hero-listen-live-btn">
                  <Radio className="w-5 h-5" strokeWidth={1.5} /> Listen live
                </Link>
                <Link to={user ? "/submit" : "/register"} className="btn-secondary" data-testid="hero-submit-btn">
                  <Upload className="w-5 h-5" strokeWidth={1.5} /> Submit your music
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {error && <div className="text-red-400 text-sm" data-testid="home-error">{error}</div>}

      <ContinueListeningCard />

      {/* Live rotation preview */}
      <section>
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="overline text-[#C5A059] mb-2">Now on rotation</div>
            <h2 className="font-serif text-[#FDFBF7] text-3xl sm:text-4xl tracking-tight">The Live Channel</h2>
          </div>
          <Link to="/radio" className="btn-ghost text-sm flex items-center gap-1" data-testid="home-view-radio">
            Open live radio <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>
        {rotation.length === 0 ? (
          <div className="card-surface p-10 text-center">
            <Radio className="w-8 h-8 text-[#C5A059] mx-auto mb-3" strokeWidth={1.5} />
            <div className="text-[#FDFBF7] font-serif text-xl mb-1">The rotation is being curated</div>
            <div className="text-[#A1A4AB] text-sm">Once our admins add tracks to the live channel, they'll appear here.</div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
            {rotation.slice(0, 10).map((t, i) => (
              <TrackCard key={t.id} track={t} list={rotation} index={i} />
            ))}
          </div>
        )}
      </section>

      {/* Featured */}
      {featured.length > 0 && (
        <section>
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="overline text-[#C5A059] mb-2 flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} /> Editor's picks</div>
              <h2 className="font-serif text-[#FDFBF7] text-3xl sm:text-4xl tracking-tight">Featured</h2>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
            {featured.map((t, i) => (
              <TrackCard key={t.id} track={t} list={featured} index={i} />
            ))}
          </div>
        </section>
      )}

      {/* Recent */}
      <section>
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="overline text-[#C5A059] mb-2">Fresh from the sanctuary</div>
            <h2 className="font-serif text-[#FDFBF7] text-3xl sm:text-4xl tracking-tight">Recently approved</h2>
          </div>
          <Link to="/music" className="btn-ghost text-sm flex items-center gap-1" data-testid="home-view-music">
            Browse all <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>
        {recent.length === 0 ? (
          <div className="card-surface p-10 text-center">
            <div className="text-[#FDFBF7] font-serif text-xl mb-1">The library is being filled</div>
            <div className="text-[#A1A4AB] text-sm mb-6">Be among the first to contribute righteous music and teachings.</div>
            <Link to={user ? "/submit" : "/register"} className="btn-primary" data-testid="home-empty-submit">
              <Upload className="w-4 h-4" strokeWidth={1.5} /> Submit your first track
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
            {recent.map((t, i) => (
              <TrackCard key={t.id} track={t} list={recent} index={i} />
            ))}
          </div>
        )}
      </section>

      {/* Brother/Sister CTA */}
      <section className="card-surface reverse-grad p-10 md:p-16 text-center">
        <div className="overline text-[#C5A059] mb-3">Brothers & Sisters</div>
        <h2 className="font-serif text-[#FDFBF7] text-3xl sm:text-4xl md:text-5xl tracking-tight max-w-2xl mx-auto">
          Your sound belongs on this station.
        </h2>
        <p className="text-[#A1A4AB] max-w-xl mx-auto mt-4">
          Upload your songs, teachings, and studies for review. Every righteous submission is one step closer to lifting His name further.
        </p>
        <div className="mt-8 flex justify-center gap-3 flex-wrap">
          <Link to={user ? "/submit" : "/register"} className="btn-primary" data-testid="home-cta-submit">
            <Upload className="w-4 h-4" strokeWidth={1.5} /> Submit for review
          </Link>
          <Link to="/music" className="btn-secondary" data-testid="home-cta-explore">
            Explore the library
          </Link>
        </div>
      </section>
    </div>
  );
}
